<template>
  <div class="pa-4">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2>Account Categorieën</h2>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Add Account Category
      </v-btn>
    </div>

    <!-- Loading State -->
    <v-progress-linear v-if="store.loading" indeterminate></v-progress-linear>

    <!-- Error State -->
    <v-alert v-if="store.error" type="error" class="mb-4">
      {{ store.error }}
    </v-alert>

    <!-- Empty State -->
    <v-card
      v-if="!store.loading && store.categories.length === 0"
      class="pa-8 text-center"
    >
      <v-icon size="64" color="grey-lighten-1" class="mb-4"
        >mdi-folder-outline</v-icon
      >
      <h3 class="text-h6 mb-2">No Account Categories</h3>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Get started by creating your first account category.
      </p>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Add Account Category
      </v-btn>
    </v-card>

    <!-- Account Categories List -->
    <v-card v-if="!store.loading && store.categories.length > 0">
      <v-list>
        <v-list-item
          v-for="category in store.categories"
          :key="category.id"
          class="border-b"
        >
          <template #prepend>
            <v-icon color="primary">mdi-folder</v-icon>
          </template>

          <v-list-item-title>{{ category.name }}</v-list-item-title>

          <template #append>
            <div class="d-flex ga-2">
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click="openEditDialog(category)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click="confirmDelete(category)"
              ></v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? "Edit Account Category" : "Add Account Category" }}
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="formData.name"
              label="Name"
              :rules="nameRules"
              required
              autofocus
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid"
            @click="save"
          >
            {{ isEditing ? "Update" : "Create" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ categoryToDelete?.name }}"? This
          action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteCategory">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useAccountCategoriesStoreRefactor } from "@/stores/accountCategoriesStoreRefactor";

const store = useAccountCategoriesStoreRefactor();

// Data
const dialog = ref(false);
const deleteDialog = ref(false);
const formValid = ref(false);
const saving = ref(false);
const deleting = ref(false);
const isEditing = ref(false);
const categoryToDelete = ref(null);

const formData = reactive({
  id: null,
  name: "",
});

// Form validation rules
const nameRules = [
  (v) => !!v || "Name is required",
  (v) => (v && v.length <= 255) || "Name must be less than 255 characters",
];

// Methods
const openCreateDialog = () => {
  isEditing.value = false;
  formData.id = null;
  formData.name = "";
  dialog.value = true;
};

const openEditDialog = (category) => {
  isEditing.value = true;
  formData.id = category.id;
  formData.name = category.name;
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
  if (formData.form) {
    formData.form.reset();
  }
};

const save = async () => {
  if (!formValid.value) return;

  saving.value = true;

  try {
    const categoryData = {
      name: formData.name,
    };

    if (isEditing.value) {
      // Include the ID in the data object for update
      categoryData.id = formData.id;
      await store.update(categoryData);
    } else {
      await store.create(categoryData);
    }

    closeDialog();
  } catch (error) {
    console.error("Error saving category:", error);
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (category) => {
  categoryToDelete.value = category;
  deleteDialog.value = true;
};

const deleteCategory = async () => {
  if (!categoryToDelete.value) return;

  deleting.value = true;

  try {
    await store.destroy(categoryToDelete.value.id);
    deleteDialog.value = false;
    categoryToDelete.value = null;
  } catch (error) {
    console.error("Error deleting category:", error);
  } finally {
    deleting.value = false;
  }
};

// Load data on mount
onMounted(async () => {
  await store.fetchAll();
});
</script>
